import { useState } from "react";
import { useQuery } from "@apollo/client";
import { askrClients, SHIFT_SEGMENTS_QUERY } from "providers/AskrProvider";
import { ReportParams, ShiftSegment, TimeRangeFinite } from "../types";
import { ONE_SECOND_OF_MS } from "./timeUtils";
import { useParams } from "react-router";
import { NO_SHIFT_SCHEDULED } from "components/Dashboard/range-selectors/steppable-range-selectors/ShiftSelector/shiftUtils";

export const useGetShifts = (
  range: TimeRangeFinite,
  machemetryId: string,
  options: {
    shouldNotUseCache?: boolean;
    /** add a buffer of 1x query duration to each side of the shifts query */
    bufferQueryRange?: boolean;
    onCompleted?: (data: ShiftSegment[]) => void;
  } = {
    shouldNotUseCache: false,
    bufferQueryRange: false,
  }
): ShiftSegment[] => {
  const { bufferQueryRange = false, shouldNotUseCache = false } = options;
  const [shiftsData, setShiftsData] = useState<ShiftSegment[]>([]);
  const { ckey: tenant } = useParams() as ReportParams;

  // * add a buffer to shift request range, as sometimes the fetched range returns shift matching its ge by lt ? bug?
  const rangeDuration =
    range.lt && range.ge ? Number(range.lt) - Number(range.ge) : 0;
  const variables = bufferQueryRange
    ? {
        ge: `${Number(range.ge) - rangeDuration}`,
        lt: `${range.lt ? Number(range.lt) + rangeDuration : range.lt}`,
        machemetrySourceName: machemetryId,
      }
    : {
        ge: `${range.ge}`,
        lt: `${range.lt}`,
        machemetrySourceName: machemetryId,
      };
  const { loading, refetch, error } = useQuery(SHIFT_SEGMENTS_QUERY, {
    skip: machemetryId === "",
    client: askrClients.get(tenant),
    variables,
    fetchPolicy: shouldNotUseCache ? "no-cache" : "cache-first",
    onError: err => {
      // refetch after a 10 second delay
      setTimeout(refetch, 10 * ONE_SECOND_OF_MS);
      console.log(
        `Shifts query gone wrong. cKey: ${tenant}, ge: ${range.ge}, lt: ${range.lt}`
      );
      console.log(`${err}: retrying in 10 seconds`);
    },
    onCompleted: data => {
      if (data?.shiftSegments && !loading && !error) {
        if (options.onCompleted) {
          options.onCompleted(data.shiftSegments);
          return;
        }
        let shiftsInRange = data.shiftSegments.filter(
          shift =>
            Number(range.ge) <= Number(shift.range.ge) &&
            Number(shift.range.lt) <= Number(range.lt)
        );

        if (shiftsInRange.length === 0) {
          // if we didn't find any matching shifts, we might be inside a longer no-shift-scheduled or "not scheduled" shift that's longer than the range, e.g. we queried a 24-hour range within a not-scheduled shift that's 48 hours long, similar to https://localhost:3000/sterisconroe/reports/eos/Molding%20%2B%20Extrusion?ge=1714561200000&lt=1714647600000&workSchedule=1
          // so we'll just return the original range as a shift
          shiftsInRange = [
            {
              shift: { name: NO_SHIFT_SCHEDULED },
              range,
            },
          ];
        }

        setShiftsData(shiftsInRange);
      }
    },
  });

  return shiftsData || [];
};
